import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Plus, Search, Trash2, X } from 'lucide-react';
import {
  createInventoryTransfer,
  getInventoryCatalog,
  getTransferRestaurants,
  listRestaurants,
} from '../services/api';
import { inventoryErrorMessage } from '../utils/inventoryErrors';
import { FEATURE_GATE_ERROR_CODE } from '../config/featureGates';
import {
  MAX_TRANSFER_LINES,
  newTransferLine,
  validateTransfer,
  isTransferValid,
  buildTransferPayload,
  applyShortages,
  normalizeRestaurantList,
  transferCounterparts,
  formatTransferQuantity,
} from '../utils/inventoryTransfers';
import useEscapeKey from '../hooks/useEscapeKey';
import './TransferStockModal.css';

const MAX_SEARCH_RESULTS = 8;

const errorCodeOf = (error) => error?.response?.data?.code || null;

const isFeatureGate = (error) =>
  error?.response?.status === 403 && error?.response?.data?.error === FEATURE_GATE_ERROR_CODE;

/**
 * Resultado de una transferencia hecha: stock antes/después en el origen y en el destino.
 * @param {{transfer: Object, t: Function}} props - Transferencia devuelta por el backend
 * @returns {JSX.Element} Resultado
 */
export function TransferResult({ transfer, t }) {
  return (
    <div className="transfer-result" data-testid="transfer-result">
      <p className="transfer-result-route">
        <strong>{transfer.fromRestaurantName || transfer.fromRestaurantId}</strong>
        <ArrowRight size={16} aria-hidden="true" />
        <strong>{transfer.toRestaurantName || transfer.toRestaurantId}</strong>
      </p>
      <div className="transfer-table-wrapper">
        <table className="transfer-table">
          <thead>
            <tr>
              <th>{t('inventory.transfers.columns.item')}</th>
              <th className="num">{t('inventory.transfers.columns.quantity')}</th>
              <th className="num">{t('inventory.transfers.columns.source')}</th>
              <th className="num">{t('inventory.transfers.columns.target')}</th>
            </tr>
          </thead>
          <tbody>
            {(transfer.lines || []).map((line) => (
              <tr key={line.itemId} data-testid={`transfer-result-line-${line.itemId}`}>
                <td>{line.name || line.itemId}</td>
                <td className="num">
                  {formatTransferQuantity(line.quantity)} {line.unit || ''}
                </td>
                <td className="num transfer-stock-change transfer-stock-change--out">
                  {formatTransferQuantity(line.sourceStockBefore)} →{' '}
                  {formatTransferQuantity(line.sourceStockAfter)}
                </td>
                <td className="num transfer-stock-change transfer-stock-change--in">
                  {formatTransferQuantity(line.targetStockBefore)} →{' '}
                  {formatTransferQuantity(line.targetStockAfter)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Modal para transferir insumos de un local a otro (RES-44, épica D2). Todo o nada: si una línea
 * no alcanza o el stock cambió en el medio, el backend no mueve nada.
 * @param {Object} props - Props
 * @param {Function} props.onClose - Cierra el modal
 * @param {Function} [props.onTransferred] - Recibe la transferencia creada (201)
 * @param {Array<{restaurantId: string, name: string}>} [props.restaurants] - Locales de la org
 *   (si no vienen se piden con `listRestaurants()`)
 * @param {Array<Object>} [props.catalogItems] - Catálogo (si no viene, `getInventoryCatalog()`)
 * @param {Object<string, Object<string, number>>} [props.stockByRestaurant] - Stock conocido por
 *   local e insumo, para mostrar lo disponible en el origen
 * @param {string} [props.initialFromRestaurantId] - Origen precargado
 * @param {string} [props.initialToRestaurantId] - Destino precargado
 * @param {Array<{itemId: string, quantity?: number}>} [props.initialItems] - Líneas precargadas
 * @returns {JSX.Element} Modal
 */
function TransferStockModal({
  onClose,
  onTransferred,
  restaurants: restaurantsProp,
  catalogItems: catalogProp,
  stockByRestaurant = {},
  initialFromRestaurantId = '',
  initialToRestaurantId = '',
  initialItems = [],
}) {
  const { t } = useTranslation();
  const [restaurants, setRestaurants] = useState(() =>
    restaurantsProp ? normalizeRestaurantList(restaurantsProp) : []
  );
  const [catalog, setCatalog] = useState(() => catalogProp || []);
  const [loading, setLoading] = useState(!restaurantsProp || !catalogProp);
  const [loadError, setLoadError] = useState(false);
  const [fromRestaurantId, setFrom] = useState(initialFromRestaurantId || '');
  const [toRestaurantId, setTo] = useState(initialToRestaurantId || '');
  const [lines, setLines] = useState(() =>
    (initialItems || []).filter((i) => i && i.itemId).map((i) => newTransferLine(i))
  );
  const [reason, setReason] = useState('');
  const [search, setSearch] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null); // { message, code }
  const [result, setResult] = useState(null);

  useEscapeKey(!submitting, onClose);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        // RES-104: `GET /restaurants` le da a un admin de sucursal solo sus locales y así no
        // tenía contraparte. Este endpoint trae todos los de la organización con `managed`; si
        // falla se sigue con los de la página (o `listRestaurants()`) y el backend decide.
        const [restaurantsData, catalogData] = await Promise.all([
          getTransferRestaurants().catch((error) => {
            console.error('Error loading transfer restaurants:', error);
            return restaurantsProp ? null : listRestaurants();
          }),
          catalogProp ? null : getInventoryCatalog(),
        ]);
        if (cancelled) return;
        if (restaurantsData) setRestaurants(normalizeRestaurantList(restaurantsData));
        if (catalogData) setCatalog(catalogData?.items || []);
      } catch (error) {
        console.error('Error loading transfer data:', error);
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // Solo al abrir: las props iniciales no se vuelven a leer
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Solo insumos activos que controlan stock (el backend rechaza STOCK_NOT_TRACKED)
  const transferable = useMemo(
    () => catalog.filter((item) => item.isActive !== false && item.trackStock !== false),
    [catalog]
  );
  const catalogById = useMemo(() => new Map(catalog.map((item) => [item.itemId, item])), [catalog]);

  const usedIds = useMemo(() => new Set(lines.map((l) => l.itemId)), [lines]);
  const searchResults = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return [];
    return transferable
      .filter((item) => !usedIds.has(item.itemId))
      .filter(
        (item) =>
          (item.name || '').toLowerCase().includes(query) ||
          (item.sku || '').toLowerCase().includes(query)
      )
      .slice(0, MAX_SEARCH_RESULTS);
  }, [search, transferable, usedIds]);

  const errors = useMemo(
    () => validateTransfer({ fromRestaurantId, toRestaurantId, lines }),
    [fromRestaurantId, toRestaurantId, lines]
  );

  const sourceStock = stockByRestaurant[fromRestaurantId] || null;
  const availableFor = (line) => {
    if (line.available !== null && line.available !== undefined) return line.available;
    if (sourceStock && sourceStock[line.itemId] !== undefined) return sourceStock[line.itemId];
    return null;
  };

  const resetSubmitState = () => {
    setSubmitError(null);
  };

  const addLine = (item) => {
    if (lines.length >= MAX_TRANSFER_LINES) return;
    setLines((prev) => [...prev, newTransferLine({ itemId: item.itemId })]);
    setSearch('');
    resetSubmitState();
  };

  const updateQuantity = (key, quantity) => {
    // Al corregir la cantidad se borra la marca de "disponible" del último INSUFFICIENT_STOCK
    setLines((prev) =>
      prev.map((line) => (line.key === key ? { ...line, quantity, available: null } : line))
    );
    resetSubmitState();
  };

  const removeLine = (key) => {
    setLines((prev) => prev.filter((line) => line.key !== key));
    resetSubmitState();
  };

  const changeFrom = (value) => {
    setFrom(value);
    setLines((prev) => prev.map((line) => ({ ...line, available: null })));
    resetSubmitState();
  };

  const changeTo = (value) => {
    setTo(value);
    resetSubmitState();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setShowErrors(true);
    if (!isTransferValid(errors) || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const transfer = await createInventoryTransfer(
        buildTransferPayload({ fromRestaurantId, toRestaurantId, lines, reason })
      );
      setResult(transfer);
      if (onTransferred) onTransferred(transfer);
    } catch (error) {
      console.error('Error creating transfer:', error?.response?.data || error);
      const code = errorCodeOf(error);
      if (code === 'INSUFFICIENT_STOCK') {
        setLines((prev) => applyShortages(prev, error.response.data.shortages));
      }
      const message = isFeatureGate(error)
        ? t('inventory.transfers.errors.featureGate')
        : inventoryErrorMessage(t, error, 'inventory.transfers.errors.generic');
      setSubmitError({ message, code });
    } finally {
      setSubmitting(false);
    }
  };

  // RES-104: un lado tiene que ser un local que el usuario administra; si el otro no lo es, acá
  // solo se ofrecen los suyos (el backend igual responde TRANSFER_SCOPE)
  const fromOptions = transferCounterparts(restaurants, toRestaurantId);
  const toOptions = transferCounterparts(restaurants, fromRestaurantId);

  const restaurantName = (id) => restaurants.find((r) => r.restaurantId === id)?.name || id;

  const formErrorKeys = showErrors ? errors.form.filter((key) => key !== 'noLines') : [];
  const lineError = (line) => (showErrors ? errors.lines[line.key] : null);

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="transfer-form" noValidate>
      <div className="modal-body">
        {loadError && (
          <div className="alert alert-error" role="alert">
            {t('inventory.transfers.modal.loadError')}
          </div>
        )}
        {submitError && (
          <div className="alert alert-error transfer-submit-error" role="alert">
            {submitError.message}
          </div>
        )}

        <div className="transfer-route">
          <div className="form-group">
            <label htmlFor="transfer-from">{t('inventory.transfers.modal.from')}</label>
            <select
              id="transfer-from"
              value={fromRestaurantId}
              onChange={(e) => changeFrom(e.target.value)}
              disabled={submitting}
            >
              <option value="">{t('inventory.transfers.modal.selectRestaurant')}</option>
              {fromOptions.map((r) => (
                <option key={r.restaurantId} value={r.restaurantId}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
          <ArrowRight className="transfer-route-arrow" size={20} aria-hidden="true" />
          <div className="form-group">
            <label htmlFor="transfer-to">{t('inventory.transfers.modal.to')}</label>
            <select
              id="transfer-to"
              value={toRestaurantId}
              onChange={(e) => changeTo(e.target.value)}
              disabled={submitting}
            >
              <option value="">{t('inventory.transfers.modal.selectRestaurant')}</option>
              {toOptions.map((r) => (
                <option key={r.restaurantId} value={r.restaurantId}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        {formErrorKeys.map((key) => (
          <p key={key} className="transfer-field-error" data-testid={`transfer-error-${key}`}>
            {t(`inventory.transfers.validation.${key}`, { max: MAX_TRANSFER_LINES })}
          </p>
        ))}

        <div className="form-group transfer-search">
          <label htmlFor="transfer-search">{t('inventory.transfers.modal.addItem')}</label>
          <div className="transfer-search-box">
            <Search size={16} aria-hidden="true" />
            <input
              id="transfer-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('inventory.transfers.modal.searchPlaceholder')}
              disabled={submitting || loading || lines.length >= MAX_TRANSFER_LINES}
              autoComplete="off"
            />
          </div>
          {searchResults.length > 0 && (
            <ul className="transfer-search-results" role="listbox">
              {searchResults.map((item) => (
                <li key={item.itemId}>
                  <button
                    type="button"
                    className="transfer-search-result"
                    data-testid={`transfer-add-${item.itemId}`}
                    onClick={() => addLine(item)}
                  >
                    <Plus size={14} aria-hidden="true" />
                    <span>{item.name}</span>
                    <span className="transfer-muted">{item.unit}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {search.trim() && searchResults.length === 0 && !loading && (
            <p className="transfer-muted">{t('inventory.transfers.modal.noResults')}</p>
          )}
          <p className="transfer-muted transfer-lines-count">
            {t('inventory.transfers.modal.linesCount', {
              count: lines.length,
              max: MAX_TRANSFER_LINES,
            })}
          </p>
        </div>

        {lines.length === 0 ? (
          <p
            className={`transfer-muted transfer-empty${showErrors ? ' transfer-field-error' : ''}`}
            data-testid="transfer-no-lines"
          >
            {t('inventory.transfers.modal.noLines')}
          </p>
        ) : (
          <ul className="transfer-lines">
            {lines.map((line) => {
              const item = catalogById.get(line.itemId);
              const available = availableFor(line);
              const error = lineError(line);
              const short = line.available !== null && line.available !== undefined;
              return (
                <li
                  key={line.key}
                  className={`transfer-line${error || short ? ' has-error' : ''}`}
                  data-testid={`transfer-line-${line.itemId}`}
                >
                  <div className="transfer-line-item">
                    <strong>{item?.name || line.itemId}</strong>
                    {available !== null && (
                      <span
                        className={`transfer-available${short ? ' is-short' : ''}`}
                        data-testid={`transfer-available-${line.itemId}`}
                      >
                        {t('inventory.transfers.modal.available', {
                          quantity: formatTransferQuantity(available),
                          unit: item?.unit || '',
                        })}
                      </span>
                    )}
                  </div>
                  <div className="transfer-line-qty">
                    <input
                      type="number"
                      min="0"
                      step="any"
                      inputMode="decimal"
                      value={line.quantity}
                      onChange={(e) => updateQuantity(line.key, e.target.value)}
                      aria-label={t('inventory.transfers.columns.quantity')}
                      disabled={submitting}
                    />
                    <span className="transfer-muted">{item?.unit || ''}</span>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => removeLine(line.key)}
                      aria-label={t('inventory.transfers.modal.removeLine')}
                      disabled={submitting}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  {error && (
                    <p
                      className="transfer-field-error"
                      data-testid={`transfer-line-error-${line.itemId}`}
                    >
                      {t(`inventory.transfers.validation.${error}`)}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <div className="form-group">
          <label htmlFor="transfer-reason">{t('inventory.transfers.modal.reason')}</label>
          <input
            id="transfer-reason"
            type="text"
            maxLength={200}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('inventory.transfers.modal.reasonPlaceholder')}
            disabled={submitting}
          />
        </div>
      </div>

      <div className="modal-actions">
        <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={submitting || loading}
          data-testid="transfer-submit"
        >
          {submitting
            ? t('inventory.transfers.modal.submitting')
            : submitError?.code === 'STOCK_CHANGED'
              ? t('inventory.transfers.modal.retry')
              : t('inventory.transfers.modal.submit')}
        </button>
      </div>
    </form>
  );

  const renderResult = () => (
    <>
      <div className="modal-body">
        <div className="alert alert-success" role="status">
          {t('inventory.transfers.modal.success', {
            from: result.fromRestaurantName || restaurantName(result.fromRestaurantId),
            to: result.toRestaurantName || restaurantName(result.toRestaurantId),
          })}
        </div>
        <TransferResult transfer={result} t={t} />
      </div>
      <div className="modal-actions">
        <button type="button" className="btn btn-primary" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
    </>
  );

  return (
    <div
      className="modal-overlay"
      onClick={() => !submitting && (result || lines.length === 0) && onClose()}
    >
      <div
        className="modal-content modal-content--wide transfer-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="transfer-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id="transfer-modal-title">
            {result
              ? t('inventory.transfers.modal.doneTitle')
              : t('inventory.transfers.modal.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="transfer-close"
            aria-label={t('common.close')}
            disabled={submitting}
          >
            <X size={24} />
          </button>
        </div>
        {result ? renderResult() : renderForm()}
      </div>
    </div>
  );
}

export default TransferStockModal;
